import type { SupportedCountry, HBAddressFieldOption } from '@honeybook/i18n';
import { US_STATES, CA_PROVINCES, AU_STATES } from '@honeybook/i18n';

export type { HBAddressFieldOption };

export interface HBAddressFieldConfig {
    /** i18n key for the postal code field label — pass to t() to get the display string. */
    postalCodeKey: string;
    /** Regex for validating the postal code format. */
    postalCodePattern: RegExp;
    /** Placeholder shown inside the postal code field (example format — not an i18n key). */
    postalCodePlaceholder: string;
    /** i18n key for the state/province/county field label — pass to t() to get the display string. */
    regionKey: string;
    /** i18n key for the state/province/county dropdown placeholder — pass to t() to get the localized string. */
    regionSelectKey: string;
    /** Whether the region field is required. false for UK (county is optional). */
    regionRequired: boolean;
    /** Options for the region dropdown. Empty for countries without a required region (e.g. GB). */
    regionOptions: HBAddressFieldOption[];
    /** i18n key for the city field label — pass to t() to get the display string. */
    cityKey: string;
}

const ADDRESS_CONFIG: Record<SupportedCountry, HBAddressFieldConfig> = {
    US: {
        postalCodeKey: 'hbAddress.postalCode.zip',
        postalCodePattern: /^\d{5}(-\d{4})?$/,
        postalCodePlaceholder: 'e.g. 94702',
        regionKey: 'hbAddress.region.state',
        regionSelectKey: 'hbAddress.region.selectState',
        regionRequired: true,
        regionOptions: US_STATES,
        cityKey: 'hbAddress.city.city'
    },
    CA: {
        postalCodeKey: 'hbAddress.postalCode.postalCode',
        postalCodePattern:
            /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
        postalCodePlaceholder: 'e.g. M5C 1G6',
        regionKey: 'hbAddress.region.province',
        regionSelectKey: 'hbAddress.region.selectProvince',
        regionRequired: true,
        regionOptions: CA_PROVINCES,
        cityKey: 'hbAddress.city.city'
    },
    GB: {
        postalCodeKey: 'hbAddress.postalCode.postcode',
        postalCodePattern:
            /^[A-Z]{1,2}[0-9][A-Z0-9]? [0-9][ABD-HJLNP-UW-Z]{2}$/i,
        postalCodePlaceholder: 'e.g. SW1A 1AA',
        regionKey: 'hbAddress.region.county',
        regionSelectKey: 'hbAddress.region.selectCounty',
        regionRequired: false,
        regionOptions: [],
        cityKey: 'hbAddress.city.city'
    },
    AU: {
        postalCodeKey: 'hbAddress.postalCode.postcode',
        postalCodePattern: /^\d{4}$/,
        postalCodePlaceholder: 'e.g. 2000',
        regionKey: 'hbAddress.region.state',
        regionSelectKey: 'hbAddress.region.selectState',
        regionRequired: true,
        regionOptions: AU_STATES,
        cityKey: 'hbAddress.city.suburb'
    }
};

/**
 * Returns locale-specific address field configuration.
 * Use to drive labels, validation patterns, and region dropdowns in address forms.
 *
 * @example
 * const config = getAddressFieldConfig('GB');
 * // config.postalCodeKey → 'hbAddress.postalCode.postcode' — pass to t()
 * // config.regionRequired  → false
 */
export function getAddressFieldConfig(
    country: SupportedCountry
): HBAddressFieldConfig {
    return ADDRESS_CONFIG[country];
}
